import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import "./video-conversation.component.scss";
import LOGO_SO_WATERMARK from "../../assets/images/logo-so-watermark.png";
import LOGO_PROFI from "../../assets/images/water-mark-profi.png";

const SUPPORTED_VIDEO_TYPES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
  "video/mp4",
];

function pickVideoMimeType(): string {
  return SUPPORTED_VIDEO_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || "video/webm";
}

const VideoConversation = forwardRef(({ setIsVideoReady, mediaStream, appId }: any, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingMimeRef = useRef<string>("video/webm");
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    if (mediaStream) {
      setStream(mediaStream);
    }
  }, [mediaStream]);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      setIsVideoReady(true);
    }
  }, [stream, setIsVideoReady]);

  useImperativeHandle(ref, () => ({
    takeSnapshot,
    startRecording,
    stopRecordingAndReturnBlob,
    stopCamera,
  }));

  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];
    stream?.getTracks().forEach((track) => track.stop());
    setStream(null);
  }, [stream]);

  const takeSnapshot = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return null;

    const context = canvas.getContext("2d");
    if (!context) return null;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const snapshotUrl = canvas.toDataURL("image/png");
    const response = await fetch(snapshotUrl);
    return response.blob();
  }, []);

  const startRecording = useCallback(() => {
    if (!stream) {
      console.warn("[VideoConversation] No stream for recording");
      return;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      return;
    }

    const videoTracks = stream.getVideoTracks();
    if (!videoTracks.length) {
      console.warn("[VideoConversation] No video track on stream");
      return;
    }

    const mimeType = pickVideoMimeType();
    recordingMimeRef.current = mimeType;
    recordingChunksRef.current = [];

    const recordStream = new MediaStream(videoTracks);
    let mediaRecorder: MediaRecorder;
    try {
      mediaRecorder = new MediaRecorder(recordStream, { mimeType });
    } catch (e) {
      console.error("[VideoConversation] MediaRecorder init failed", e);
      try {
        mediaRecorder = new MediaRecorder(recordStream);
        recordingMimeRef.current = mediaRecorder.mimeType || "video/webm";
      } catch (e2) {
        console.error("[VideoConversation] MediaRecorder fallback failed", e2);
        return;
      }
    }

    mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordingChunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
  }, [stream]);

  const stopRecordingAndReturnBlob = (): Promise<Blob | null> => {
    return new Promise<Blob | null>((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state === "inactive") {
        const chunks = recordingChunksRef.current;
        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;
        if (chunks.length === 0) {
          resolve(null);
          return;
        }
        resolve(new Blob(chunks, { type: recordingMimeRef.current }));
        return;
      }

      mediaRecorder.onstop = () => {
        const chunks = recordingChunksRef.current;
        recordingChunksRef.current = [];
        mediaRecorderRef.current = null;

        if (chunks.length === 0) {
          resolve(null);
          return;
        }
        resolve(new Blob(chunks, { type: recordingMimeRef.current }));
      };

      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error("[VideoConversation] Error stopping MediaRecorder:", error);
        resolve(null);
      }
    });
  };

  return (
    <div className="video-comp-control">
      <VideoPreview appId={appId} videoRef={videoRef} canvasRef={canvasRef} />
    </div>
  );
});

function VideoPreview({
  appId,
  videoRef,
  canvasRef,
}: {
  appId: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}) {
  return (
    <div className="video-comp-border">
      <div className="video-comp-border-inside">
        {appId === "SCOUTOUT" ? (
          <img src={LOGO_SO_WATERMARK} className="watermark" alt="" />
        ) : appId === "PROFI" ? (
          <img src={LOGO_PROFI} className="watermark-custom" alt="" />
        ) : null}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            objectFit: "cover",
            borderRadius: "24px",
            transform: "scaleX(-1)",
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} width={640} height={480} />
      </div>
    </div>
  );
}

export default VideoConversation;
